<template>
  <div
    v-if="showBanner"
    class="fixed top-0 left-0 right-0 z-50 shadow-lg transition-all duration-300"
    :class="bannerClasses"
  >
    <!-- Minimized View -->
    <div
      v-if="minimized"
      class="flex items-center justify-between px-4 h-8 cursor-pointer"
      @click="minimized = false"
    >
      <span class="text-sm font-medium flex items-center">
        <svg v-if="isRunning" class="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span v-if="isComplete && progress.status === 'completed'" class="mr-2">Done:</span>
        <span v-else-if="isComplete && progress.status === 'failed'" class="mr-2">Failed:</span>
        <span v-else-if="isComplete && progress.status === 'cancelled'" class="mr-2">Cancelled:</span>
        Syncing {{ progress.processed }}/{{ progress.total }} campaigns
      </span>
      <span class="text-sm font-semibold">{{ progress.progress_percent }}%</span>
    </div>

    <!-- Full View -->
    <div v-else class="p-4">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-lg font-semibold flex items-center">
          <!-- Running spinner -->
          <svg v-if="isRunning" class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <!-- Completed icon -->
          <svg v-else-if="progress.status === 'completed'" class="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <!-- Failed icon -->
          <svg v-else-if="progress.status === 'failed'" class="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
          <!-- Cancelled icon -->
          <svg v-else-if="progress.status === 'cancelled'" class="h-5 w-5 mr-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
          </svg>
          <span>{{ bannerTitle }}</span>
        </h3>
        <div class="flex items-center space-x-2">
          <!-- Minimize button -->
          <button
            @click="minimized = true"
            class="p-1 rounded hover:bg-white/20 transition-colors"
            title="Minimize"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"></path>
            </svg>
          </button>
          <!-- Cancel button (only when running) -->
          <button
            v-if="isRunning"
            @click="handleCancel"
            :disabled="cancelling"
            class="p-1 rounded hover:bg-white/20 transition-colors disabled:opacity-50"
            title="Cancel sync"
          >
            <svg v-if="cancelling" class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z"></path>
            </svg>
          </button>
          <!-- Dismiss button (only when complete) -->
          <button
            v-if="isComplete"
            @click="handleDismiss"
            class="p-1 rounded hover:bg-white/20 transition-colors"
            title="Dismiss"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Progress Bar -->
      <div class="relative w-full rounded-full h-4 mb-2" :class="progressBarBgClass">
        <div
          class="rounded-full h-4 transition-all duration-300"
          :class="progressBarFillClass"
          :style="{ width: progress.progress_percent + '%' }"
        ></div>
        <span class="absolute inset-0 flex items-center justify-center text-xs font-semibold" :class="progressTextClass">
          {{ progress.processed }}/{{ progress.total }} ({{ progress.progress_percent }}%)
        </span>
      </div>

      <!-- Status Line -->
      <div class="flex items-center justify-between text-sm">
        <span class="truncate max-w-md">{{ statusMessage }}</span>
        <div class="flex items-center space-x-3">
          <span v-if="progress.successful > 0" class="flex items-center">
            <svg class="w-4 h-4 mr-1 text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            {{ progress.successful }} successful
          </span>
          <span v-if="progress.failed > 0" class="flex items-center">
            <svg class="w-4 h-4 mr-1 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            {{ progress.failed }} failed
          </span>
          <span v-if="isRunning && progress.eta_readable">
            ~{{ progress.eta_readable }} remaining
          </span>
        </div>
      </div>

      <!-- Error message if present -->
      <div v-if="error" class="mt-2 p-2 bg-red-900/50 rounded text-sm">
        <strong>Error:</strong> {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'BulkSyncProgressBanner',

  data() {
    return {
      minimized: false,
      cancelling: false,
      autoDismissTimer: null,
    };
  },

  computed: {
    ...mapState('salesforceSync', ['isRunning', 'sessionId', 'progress', 'error']),
    ...mapGetters('salesforceSync', ['isComplete', 'statusMessage']),

    /**
     * Whether to show the banner
     */
    showBanner() {
      return this.sessionId !== null;
    },

    /**
     * Banner title based on status
     */
    bannerTitle() {
      if (this.isComplete) {
        if (this.progress.status === 'completed') {
          return `Sync Complete: ${this.progress.total} campaigns`;
        } else if (this.progress.status === 'failed') {
          return 'Sync Failed';
        } else if (this.progress.status === 'cancelled') {
          return 'Sync Cancelled';
        }
      }
      return `Syncing ${this.progress.total} campaigns to Salesforce`;
    },

    /**
     * CSS classes for the banner based on status
     */
    bannerClasses() {
      if (this.progress.status === 'completed') {
        return 'bg-gradient-to-r from-green-600 to-green-700 text-white';
      } else if (this.progress.status === 'failed') {
        return 'bg-gradient-to-r from-red-600 to-red-700 text-white';
      } else if (this.progress.status === 'cancelled') {
        return 'bg-gradient-to-r from-amber-600 to-amber-700 text-white';
      }
      // Running
      return 'bg-gradient-to-r from-blue-600 to-blue-700 text-white';
    },

    /**
     * Progress bar background class
     */
    progressBarBgClass() {
      if (this.progress.status === 'completed') {
        return 'bg-green-800';
      } else if (this.progress.status === 'failed') {
        return 'bg-red-800';
      } else if (this.progress.status === 'cancelled') {
        return 'bg-amber-800';
      }
      return 'bg-blue-800';
    },

    /**
     * Progress bar fill class
     */
    progressBarFillClass() {
      if (this.progress.status === 'completed') {
        return 'bg-green-300';
      } else if (this.progress.status === 'failed') {
        return 'bg-red-300';
      } else if (this.progress.status === 'cancelled') {
        return 'bg-amber-300';
      }
      return 'bg-white';
    },

    /**
     * Progress text class
     */
    progressTextClass() {
      if (this.progress.status === 'completed') {
        return 'text-green-900';
      } else if (this.progress.status === 'failed') {
        return 'text-red-900';
      } else if (this.progress.status === 'cancelled') {
        return 'text-amber-900';
      }
      return 'text-blue-900';
    },
  },

  watch: {
    /**
     * Watch for sync completion to set up auto-dismiss
     */
    isComplete(complete) {
      if (complete) {
        // Auto-dismiss after 30 seconds
        this.autoDismissTimer = setTimeout(() => {
          this.handleDismiss();
        }, 30000);
      }
    },
  },

  mounted() {
    // Restore session from localStorage on mount
    this.restoreSession();
  },

  beforeUnmount() {
    // Clear auto-dismiss timer
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
    }
  },

  methods: {
    ...mapActions('salesforceSync', ['cancelSync', 'dismissSession', 'restoreSession']),

    /**
     * Handle cancel button click
     */
    async handleCancel() {
      if (this.cancelling) return;

      this.cancelling = true;
      try {
        await this.cancelSync();
      } catch (error) {
        console.error('Failed to cancel sync:', error);
      } finally {
        this.cancelling = false;
      }
    },

    /**
     * Handle dismiss button click
     */
    handleDismiss() {
      if (this.autoDismissTimer) {
        clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = null;
      }
      this.dismissSession();
    },
  },
};
</script>
